import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { prefixFieldNameIfPrefixExists } from '@src/common/utils/formUtils';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { FormDimensionInput } from '@src/orders/PermitFormSections/FormDimensionInput';
import { FormWeightInput } from '@src/orders/PermitFormSections/FormWeightInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TrailerDeckHeightField,
  TrailerEmptyWeightField,
  TrailerGroundClearanceField,
  TrailerHeightField,
  TrailerKingpinSetbackLengthField,
  TrailerKingpinSettingLengthField,
  TrailerLengthField,
  TrailerRegisteredWeightField,
  TrailerWheelbaseLengthField,
  TrailerWidthField,
} from '@src/permitField/TrailerDimensionFields';
import type { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import type { TFunction } from 'i18next';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

type Props = {
  control: Control<yup.InferType<typeof TrailerFormSchema>>;
  readOnly?: boolean;
  fieldsPrefix?: string;
  fields: string[];
};

export const TrailerDimensionsForm = ({
  control,
  readOnly,
  fieldsPrefix,
  fields,
}: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  if (fields.length <= 0) {
    return null;
  }

  const weightSectionFields: string[] = [
    TrailerEmptyWeightField.fieldName,
    TrailerRegisteredWeightField.fieldName,
  ];
  const showWeightSection = fields.some((field) =>
    weightSectionFields.includes(field),
  );

  return (
    <Stack gap={theme.spacing['3']}>
      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={fields}
          conditions={TrailerLengthField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly || isFieldDisabled(TrailerLengthField.fieldName),
              fieldName: TrailerLengthField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
        <ConditionalField
          fields={fields}
          conditions={TrailerWidthField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly || isFieldDisabled(TrailerWidthField.fieldName),
              fieldName: TrailerWidthField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
      </Stack>
      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={fields}
          conditions={TrailerHeightField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly || isFieldDisabled(TrailerHeightField.fieldName),
              fieldName: TrailerHeightField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
        <ConditionalField
          fields={fields}
          conditions={TrailerWheelbaseLengthField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly ||
                isFieldDisabled(TrailerWheelbaseLengthField.fieldName),
              fieldName: TrailerWheelbaseLengthField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
      </Stack>
      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={fields}
          conditions={TrailerDeckHeightField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly || isFieldDisabled(TrailerDeckHeightField.fieldName),
              fieldName: TrailerDeckHeightField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
        <ConditionalField
          fields={fields}
          conditions={TrailerGroundClearanceField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly ||
                isFieldDisabled(TrailerGroundClearanceField.fieldName),
              fieldName: TrailerGroundClearanceField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
      </Stack>
      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={fields}
          conditions={TrailerKingpinSetbackLengthField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly ||
                isFieldDisabled(TrailerKingpinSetbackLengthField.fieldName),
              fieldName: TrailerKingpinSetbackLengthField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
        <ConditionalField
          fields={fields}
          conditions={TrailerKingpinSettingLengthField.fieldName}
        >
          <FormDimensionInput
            {...customMetricInputProps({
              t,
              readOnly:
                readOnly ||
                isFieldDisabled(TrailerKingpinSettingLengthField.fieldName),
              fieldName: TrailerKingpinSettingLengthField.fieldName,
              trailerFieldPrefix: fieldsPrefix,
              control,
            })}
          />
        </ConditionalField>
      </Stack>
      {showWeightSection && (
        <Stack>
          <Typography
            variant="subtitle2"
            style={{ marginTop: theme.spacing['8'] }}
          >
            {t('trailerWeight')}
          </Typography>
          <Divider />
          <Stack
            direction="row"
            gap={theme.spacing['3']}
            marginTop={theme.spacing['6']}
          >
            <ConditionalField
              fields={fields}
              conditions={TrailerEmptyWeightField.fieldName}
            >
              <FormWeightInput
                {...customMetricInputProps({
                  t,
                  readOnly:
                    readOnly ||
                    isFieldDisabled(TrailerEmptyWeightField.fieldName),
                  fieldName: TrailerEmptyWeightField.fieldName,
                  trailerFieldPrefix: fieldsPrefix,
                  control,
                })}
              />
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={TrailerRegisteredWeightField.fieldName}
            >
              <FormWeightInput
                {...customMetricInputProps({
                  t,
                  readOnly:
                    readOnly ||
                    isFieldDisabled(TrailerRegisteredWeightField.fieldName),
                  fieldName: TrailerRegisteredWeightField.fieldName,
                  trailerFieldPrefix: fieldsPrefix,
                  control,
                })}
              />
            </ConditionalField>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const customMetricInputProps = <T extends FieldValues>({
  t,
  control,
  fieldName,
  readOnly,
  trailerFieldPrefix,
  isNumerical,
}: {
  t: TFunction;
  control: Control<T>;
  fieldName: FieldPath<T>;
  readOnly?: boolean;
  trailerFieldPrefix?: string;
  isNumerical?: boolean;
}) => ({
  label: t(`fields.${fieldName}`),
  fieldName,
  disabled: readOnly,
  type: isNumerical ? 'number' : 'text',
  name: prefixFieldNameIfPrefixExists<T>(fieldName, trailerFieldPrefix),
  control,
});
